import { takeEvery, call, put, select, take, delay, race, all } from 'redux-saga/effects';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { isBefore } from 'date-fns';

import URLS from 'constants/urls';
import { PERMISSION, VENUE_SERVICE_TYPES } from 'constants/logic';
import { makePath } from 'helpers/link-generation';
import ls, { fetchStorageForUser, overwriteStorageForUser, getKeyForUser } from 'helpers/local-storage';
import { reportWarning } from 'helpers/error-logging';
import { forceRedirectOutsideReact } from 'helpers/page-utils';
import Segment from 'helpers/segment';
import { isSkuVenue, isVenueDashboardSubtypeFoodAndBeverage } from 'helpers/ia-logic';
import { persistNextUrlForLogin } from 'helpers/session-auth-redirect';
import { VENUE_FEATURE_PROBE_HTTP_CONFIG } from 'helpers/session-auth-http';
import { logOut, userInfo, venueSmartspotData } from 'store/saga/entities/bound';
import {
  selectActiveVenue,
  selectSchedulesStartDate,
  selectRouteParamStartDate,
  selectUserInfo,
  selectUserIsAuthenticated,
  selectVenueSmartspotDataIsLoadedByVenueId,
  selectVenueSmartspotDataIsLoadingByVenueId,
  selectVenueSmartspotEnabledByVenueId,
} from 'selectors/general';

import { selectAppInitialized, selectGitSha } from 'selectors/app';
import { setAppInitialized, setSchedulesStartDate, setForceRefreshOnRouteChange } from 'reducers/app';
import { SET_PAGE_VISIBILITY } from 'reducers/ui';
import { setDesktopCollapsed } from 'features/Sidenav/reducers';
import { LOCAL_STORAGE_KEY_DESKTOP_NAV_IS_COLLPASED } from 'features/Sidenav/saga';

const ONE_HOUR = 1000 * 60 * 60;
const SIX_HOURS = ONE_HOUR * 6;

export const CROSS_TAB_DISPATCH = 'CROSS_TAB_DISPATCH';

export const CHANGE_ACTIVE_VENUE = 'CHANGE_ACTIVE_VENUE';
export const ACTIVE_VENUE_CHANGED = 'ACTIVE_VENUE_CHANGED';
export const DISPLAY_BANNER = 'DISPLAY_BANNER';
export const POLL_FOR_APP_UPDATES = 'POLL_FOR_APP_UPDATES';
export const VALIDATE_ACTIVE_USER = 'VALIDATE_ACTIVE_USER';
export const SESSION_AUTH_EXPIRED = 'SESSION_AUTH_EXPIRED';

export const sessionAuthExpired = status => ({ type: SESSION_AUTH_EXPIRED, status });

export const changeActiveVenue = venue => ({ type: CHANGE_ACTIVE_VENUE, venue });
export const activeVenueChanged = (venueId, userId, permissions) => ({
  type: ACTIVE_VENUE_CHANGED,
  venueId,
  userId,
  permissions,
});
export const displayBanner = (bannerFn, bannerMsg) => ({ type: DISPLAY_BANNER, bannerFn, bannerMsg });
export const pollForAppUpdates = () => ({ type: POLL_FOR_APP_UPDATES });

export const venueSelection = venueId => ({ selectedVenueId: venueId });

function initFullStory(user) {
  if (window.FS && window.FS.identify) {
    const { userId, firstName, lastName, email } = user;
    window.FS.identify(userId, {
      displayName: [lastName, firstName].join(', '),
      email,
    });
  }
}

function initSegment(user, venue) {
  // Only fire Identify when the venue insights fetch succeeded; a failed fetch yields null
  // (see getVenueIdentifyAttributes) and we skip the event rather than send a partial profile.
  if (!venue?.venueIdentifyAttributes) return;

  const { adminUserId, permissions, role, userId, venuesList } = user;
  const {
    autosyncStatus,
    calendarConfigurationSource,
    countryCode,
    currencyCode,
    enabled,
    isManualStudio,
    isSmartspotEligible,
    serviceTypes,
    smartspotCannotManageEnabled,
    smartspotVenueEnabled,
    timezone,
    venueCreationDate,
    venueId,
    venueSource,
    venueIdentifyAttributes,
  } = venue;

  Segment.identify(userId, {
    'active venue id': venueId,
    'venue count': venuesList.length,
    'venue creation date': venueCreationDate,
    'venue country': countryCode,
    'venue currency': currencyCode,
    'venue timezone': timezone,
    'venue enabled status': enabled,
    'has schedule type class': serviceTypes?.includes(VENUE_SERVICE_TYPES.CLASS),
    'has schedule type appointment': serviceTypes?.includes(VENUE_SERVICE_TYPES.APPOINTMENT),
    'venue is integrated': !isManualStudio,
    'venue integration source': venueSource,
    'venue is autosync': autosyncStatus,
    'venue calendarsync source': calendarConfigurationSource,
    'venue msa id': userInfo.venuesList?.find(item => item?.venueId === venueId)?.msaId,
    'user can edit smartspot for venue':
      isSmartspotEligible && !smartspotCannotManageEnabled && permissions.includes(PERMISSION.CAN_MANAGE_SMARTSPOT),
    'smartspot venue enabled': smartspotVenueEnabled,
    'user permissions': permissions,
    'user role': role,
    'is impersonate': !!adminUserId,
    'venue is food and beverage': isVenueDashboardSubtypeFoodAndBeverage(venue),
    'has alpha venue access': venuesList.some(item => isSkuVenue(item)),
    ...venueIdentifyAttributes,
  });
}

function initBraze(user) {
  // While impersonating, the Braze Web SDK would attribute the admin's session — language,
  // country, timezone collected from their browser/IP — onto the partner's profile, corrupting
  // the language their transactional emails render in (ST-8817). Skip binding the SDK entirely.
  if (user.adminUserId) return;

  window.appboy?.changeUser?.(user.userId);
  window.appboy?.getUser?.().setFirstName?.(user.firstName);
  window.appboy?.getUser?.().setLastName?.(user.lastName);
  window.appboy?.openSession?.();
  window.appboy?.display?.automaticallyShowNewInAppMessages?.();
}

export function closeBrazeSession() {
  window.appboy?.destroy();
}

export function* doValidateActiveUser(dependencies, otherTabUser) {
  const thisTabUser = yield select(selectUserInfo);
  if (thisTabUser.isAuthenticated !== otherTabUser.isAuthenticated || thisTabUser.userId !== otherTabUser.userId) {
    yield call(forceRedirectOutsideReact, makePath.login());
  }
}

// used to communicate between tabs
export function watchLocalStorageMessages(dispatch) {
  ls.on(CROSS_TAB_DISPATCH, value => {
    if (value && value.type) {
      dispatch(value);
    }
  });
}

export function* doPollForAppUpdates(dependencies) {
  /*
    We check for changes every hour.
    Once a change has been detected, give the user 6 hours to refresh on their own.
    After 6 hours...
     - refresh during a route change
     - refresh if the tab moves to the background
     - refresh if an hour passes
  */
  const { httpClient } = dependencies;

  yield delay(ONE_HOUR);

  const currentSha = yield select(selectGitSha);
  const { data: nextSha } = yield call([httpClient, httpClient.get], '/sha');

  if (currentSha === nextSha) {
    yield put(pollForAppUpdates());
  } else {
    yield delay(SIX_HOURS);
    yield put(setForceRefreshOnRouteChange());
    while (true) {
      const [{ foreground: pageInForeground } = {}, delayExpired = false] = yield race([
        take(SET_PAGE_VISIBILITY),
        delay(ONE_HOUR, true),
      ]);
      if (delayExpired || !pageInForeground) {
        break;
      }
    }
    yield call(forceRedirectOutsideReact, window.location);
  }
}

/**
 * This function handles the loading of SmartSpot venue data from state or by calling the entity if not in state.
 *
 * First, it checks if the SmartSpot data is already loaded or currently loading.
 * If the data is not loaded but is currently loading, it waits for the data to load or for a timeout to occur.
 * If the data is not loaded and not currently loading, it fetches the venueSmartspotData entity.
 * If venueSmartspotEnabled is not set in the previous step, the function selects the SmartSpot data from the state.
 *
 * Explicity calls SmartSpot selectors with venue ID to account for delay when active venue is changed.
 *
 * @returns boolean || undefined: The SmartSpot venue enabled status or undefined if there's no SmartSpot data for a venue.
 */
function* getSmartspotVenueEnabled(dependencies, startDate, venueId) {
  const isSmartspotLoading = yield select(selectVenueSmartspotDataIsLoadingByVenueId, venueId);
  const isSmartspotLoaded = yield select(selectVenueSmartspotDataIsLoadedByVenueId, venueId);

  let smartspotVenueEnabled;
  if (!isSmartspotLoaded) {
    if (isSmartspotLoading) {
      while (true) {
        const { success, timeout = false } = yield race({
          success: take(venueSmartspotData.successAction().type),
          timeout: delay(5000),
        });

        if (success || timeout) {
          break;
        }
      }
    } else {
      const { isEnabled } = yield call(fetchEntity, venueSmartspotData, [
        dependencies,
        { meta: { venueId, date: startDate } },
      ]);
      smartspotVenueEnabled = isEnabled;
    }
  }

  if (smartspotVenueEnabled === undefined) {
    smartspotVenueEnabled = yield select(selectVenueSmartspotEnabledByVenueId, venueId);
  }
  return smartspotVenueEnabled;
}

// Venue-state identify traits are computed server-side (ST-8767) and consumed via a single
// authenticated GET per venue switch. The call opts out of the global session-auth redirect
// (401/403) and internal-error tracking (5xx) so this analytics fetch can never log the user
// out or surface an error.
const fetchVenueInsights = (httpClient, venueId) =>
  httpClient
    .get(URLS.venue.venueInsights(venueId), { ...VENUE_FEATURE_PROBE_HTTP_CONFIG, ignoreInternalError: true })
    .then(response => response?.data?.traits ?? {});

export function* getVenueIdentifyAttributes(dependencies, venue) {
  const { httpClient } = dependencies;
  const { venueId } = venue;

  try {
    return yield call(fetchVenueInsights, httpClient, venueId);
  } catch (error) {
    yield call(reportWarning, 'identify-attributes: venue insights failed', { message: error?.message });
    return null;
  }
}

export function* getVenueTrackingData(dependencies, venue) {
  const { venueId, startDate = new Date() } = venue;
  if (!venueId) return null;
  const smartspotVenueEnabled = yield call(getSmartspotVenueEnabled, dependencies, startDate, venueId);
  const venueIdentifyAttributes = yield call(getVenueIdentifyAttributes, dependencies, venue);
  return { ...venue, smartspotVenueEnabled, venueIdentifyAttributes };
}

export function* handleRootRouteEnter(dependencies) {
  const appInitialized = yield select(selectAppInitialized);
  if (appInitialized) return;

  const user = yield call(fetchEntityIfNeeded, userInfo, [dependencies]);
  const { userId, isAuthenticated = false } = user;

  if (isAuthenticated) {
    const venue = yield select(selectActiveVenue);
    const venueDataForSegment = yield call(getVenueTrackingData, dependencies, venue);
    if (venueDataForSegment) {
      initSegment(user, venueDataForSegment);
    }

    if (['production', 'test'].includes(process.env.NODE_ENV)) {
      initBraze(user);
      initFullStory(user);
    }
  }

  // poll deployed versions to force a page refresh to prevent FE/BE version mismatches
  if (isAuthenticated) {
    yield put(pollForAppUpdates());
  }

  yield call(ls.set, CROSS_TAB_DISPATCH, { type: VALIDATE_ACTIVE_USER, userId, isAuthenticated });

  const desktopNavIsCollapsed = yield call(getKeyForUser, userId, LOCAL_STORAGE_KEY_DESKTOP_NAV_IS_COLLPASED);
  yield put(setDesktopCollapsed(desktopNavIsCollapsed));

  yield put(setAppInitialized());
}

export function* waitForAppInit() {
  while (true) {
    const appInitialized = yield select(selectAppInitialized);
    if (appInitialized) break;
    yield delay(1);
  }
}

export function* waitForNavigate(client) {
  while (!client.navigate || !client.location) {
    yield delay(1);
  }
}

export function* doChangeActiveVenue(dependencies, action) {
  const { venue } = action;
  const { venueId } = venue;

  const user = yield call(fetchEntityIfNeeded, userInfo, [dependencies]);
  const { userId, isAuthenticated = false, permissions } = user;
  const userStorage = yield call(fetchStorageForUser, userId);
  const newUserStorage = Object.assign(userStorage, {
    selectedVenueId: venueId,
  });
  yield call(overwriteStorageForUser, userId, newUserStorage);

  if (isAuthenticated) {
    const venueDataForSegment = yield call(getVenueTrackingData, dependencies, venue);
    initSegment(user, venueDataForSegment);
  }

  yield all([put(activeVenueChanged(venueId, userId, permissions)), call(Segment.event, 'Venue Change', { venueId })]);
}

export function* redirectUnauthenticated() {
  const isAuthenticated = yield select(selectUserIsAuthenticated);
  if (!isAuthenticated) {
    yield call(forceRedirectOutsideReact, '/');
  }
}

export function* redirectUnpermissioned(dependencies) {
  yield call(waitForNavigate, dependencies);
  try {
    yield call(dependencies.navigate, '/login');
  } catch (error) {
    yield call(forceRedirectOutsideReact, '/login');
  }
}

export function* handleSessionAuthExpired(dependencies, action) {
  // Only persist the "next" URL on 401 (session genuinely expired — user wanted to be there).
  // On 403 (forbidden), sending them back after re-login would trip the same 403 and trap the user
  // in a redirect loop.
  if (action?.status === 401) {
    yield call(persistNextUrlForLogin);
  }

  try {
    yield call(fetchEntity, logOut, [dependencies]);
  } catch (error) {
    // 401/403 means the session was already invalid — expected; proceed silently.
    const status = error?.response?.status;
    if (status !== 401 && status !== 403) {
      yield call(reportWarning, 'logOut failed during session-auth-expired flow', {
        status,
        message: error?.message,
      });
    }
  }

  yield call(closeBrazeSession);
  yield call(forceRedirectOutsideReact, makePath.login());
}

export function* doDisplayBanner(action) {
  const { bannerFn, bannerMsg } = action;
  yield put(bannerFn(bannerMsg));
}

export function* getStartDateOrToday() {
  const today = new Date();
  let startDate = yield select(selectRouteParamStartDate);

  if (startDate) {
    yield put(setSchedulesStartDate(startDate));
    return startDate;
  }

  startDate = yield select(selectSchedulesStartDate);
  if (startDate && !isBefore(startDate, today)) {
    yield put(setSchedulesStartDate(startDate));
    return startDate;
  }

  yield put(setSchedulesStartDate(today));
  return today;
}

export function* watchApp(dependencies) {
  yield takeEvery(CHANGE_ACTIVE_VENUE, doChangeActiveVenue, dependencies);
  yield takeEvery(DISPLAY_BANNER, doDisplayBanner);
  yield takeEvery(POLL_FOR_APP_UPDATES, doPollForAppUpdates, dependencies);
  yield takeEvery(VALIDATE_ACTIVE_USER, doValidateActiveUser, dependencies);
  yield takeEvery(SESSION_AUTH_EXPIRED, handleSessionAuthExpired, dependencies);
}
